import he from 'he';
import fp from 'lodash/fp';
import React from 'react';
import { Button, Col, Modal, Row } from 'react-bootstrap';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import Spinner from 'react-spinkit';
import { branch, defaultProps, lifecycle, renderComponent, withHandlers, withProps, withStateHandlers } from 'recompose';
import { bindActionCreators, compose } from 'redux';
import { defaultTeamQuery, routes } from '../../../../../constants';
import { fullName, isBusinessAdmin, isTeamManager } from '../../../../../services';
import { businessActions, businessCurrentTeamSelector, businessSelector, businessTeamsSelector, teamMembersSelector } from '../../../../../state/modules/business';
import { currentUserSelector } from '../../../../../state/modules/user';
import { noCapMap, pluralise } from '../../../../../utils';
import { ConfirmationModal, Icon, SearchForm, TeamAvatar } from '../../../../components';
import Loading from '../../../loading';
import { TeamEditCreate } from '../components';

const Teams = compose(
  withStateHandlers(
    {
      isCreateTeamModalOpen: false
    },
    {
      toggleCreateTeamModal: ({ isCreateTeamModalOpen }) => () => ({
        isCreateTeamModalOpen: !isCreateTeamModalOpen
      })
    }
  ),
  withHandlers({
    handleTeamCreated: ({ toggleCreateTeamModal, resetSearchQuery }) => () => {
      resetSearchQuery()
      toggleCreateTeamModal()
    }
  })
)(
  ({ rows, toggleCreateTeamModal, handleTeamCreated, isCreateTeamModalOpen, isTeamManager, searchQuery, onSearchChange, hasMoreToLoad, loadMoreTeams, isFetchingTeams }) => (
    <Row className='no-gutter teams-settings'>
      <Col xs={12}>
        <h2>Teams</h2>
      </Col>
      <Col sm={12} md={4}>
        <SearchForm label="Search business teams" searchQuery={searchQuery} onSearchChange={onSearchChange} />
      </Col>
      {
        isTeamManager ? null :
          <Col xs={12} sm={4} smOffset={8} md={2} mdOffset={10}>
            <Modal show={isCreateTeamModalOpen} onHide={toggleCreateTeamModal} dialogClassName='create-team-modal'>
              <Modal.Body>
                <Button onClick={toggleCreateTeamModal} className='pull-right full-width' bsStyle='plain'>
                  <Icon icon='close' />
                </Button>
                <TeamEditCreate callback={handleTeamCreated} />
              </Modal.Body>
            </Modal>
            <Button className='full pull-right bottom-gap' onClick={toggleCreateTeamModal}>
              <Icon icon='add-large' />
              &nbsp;
              Create Team
            </Button>
          </Col>
      }
      <Col xs={12}>
        {
          isFetchingTeams && rows.length === 0 ?
            <div className='flex-center'><Loading /></div> :
            rows
        }
      </Col>
      {
        hasMoreToLoad ?
          <Col xs={12} className='flex-center top-gap'>
            {
              isFetchingTeams ?
                <Button><Spinner name='circle' className='spinner' fadeIn='none' /></Button> :
                <Button onClick={loadMoreTeams}>Load More</Button>
            }
          </Col> : null
      }
    </Row>
  )
);

const TeamManagerTeams = compose(
  withProps(
    ({ user, getTeam }) => {
      const {
        businessId,
        businessTeamId
      } = fp.first(user.teamMemberRoles);
      const refetchTeam = () => getTeam(businessId, businessTeamId);
      return {
        refetchTeam
      }
    }
  ),
  lifecycle({
    componentDidMount() {
      const {
        refetchTeam
      } = this.props;
      refetchTeam()
    }
  }),
  branch(
    ({ team }) => !team,
    renderComponent(Loading)
  ),
  withStateHandlers(
    {
      isEditModalOpen: false
    },
    {
      toggleEditModal: ({ isEditModalOpen }) => () => ({
        isEditModalOpen: !isEditModalOpen
      })
    }
  ),
  withHandlers({
    handleTeamEdited: ({ toggleEditModal, resetSearchQuery, refetchTeam }) => () => {
      resetSearchQuery()
      toggleEditModal()
      refetchTeam()
    }
  }),
  withProps(
    ({ team, isEditModalOpen, toggleEditModal, handleTeamEdited }) => ({
      rows: fp.flow(
        noCapMap(
          ({ businessTeam, members }, rid) => <Row key={rid} className='no-gutter striped'>
            <Col xs={12} sm={10}>
              <p>
                <TeamAvatar teamName={he.decode(businessTeam.name)} />
                {he.decode(businessTeam.name)}
                <br />
                <small>{members.length} team {pluralise('member', members.length)}</small>
              </p>
            </Col>
            <Col xs={12} sm={2}>
              <Modal show={isEditModalOpen} onHide={toggleEditModal} dialogClassName='create-team-modal'>
                <Modal.Body>
                  <Button onClick={toggleEditModal} className='pull-right full-width' bsStyle='plain'>
                    <Icon icon='close' />
                  </Button>
                  <TeamEditCreate
                    isTeamManager={true}
                    currentBusinessTeam={team.businessTeam}
                    existingTeamMembers={team.members}
                    callback={handleTeamEdited} />
                </Modal.Body>
              </Modal>
              <p>
                <Button bsStyle='plain' className='full align-right' onClick={toggleEditModal}>Edit</Button>
              </p>
            </Col>
          </Row >
        )
      )([team])
    })
  ),
  defaultProps({
    isTeamManager: true
  })
)(Teams)

const mapStateToProps = (state) => ({
  user: currentUserSelector(state),
  business: businessSelector(state),
  businessTeams: businessTeamsSelector(state),
  isFetchingTeams: state.business.isFetchingTeams,
  team: businessCurrentTeamSelector(state),
  teamMembers: teamMembersSelector(state)
})
const mapDispatchToProps = (dispatch) => ({
  getTeams: bindActionCreators(businessActions.business.team.getTeams, dispatch),
  loadMoreTeams: bindActionCreators(businessActions.business.team.loadMoreTeams, dispatch),
  getTeam: bindActionCreators(businessActions.business.team.getTeam, dispatch),
  removeTeam: bindActionCreators(businessActions.business.team.removeTeam, dispatch),
  addTeamManager: bindActionCreators(businessActions.business.team.addTeamManager, dispatch),
  removeTeamManager: bindActionCreators(businessActions.business.team.removeTeamManager, dispatch)
})

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withStateHandlers(
    {
      searchQuery: '',
      refetch: true
    },
    {
      onSearchChange: () => (e) => ({
        refetch: true,
        searchQuery: e.target.value
      }),
      resetSearchQuery: () => () => ({
        refetch: false,
        searchQuery: ''
      })
    }
  ),
  branch(
    ({ user, business }) => isTeamManager(user, business) && !isBusinessAdmin(user),
    renderComponent(TeamManagerTeams)
  ),
  withProps(
    ({ searchQuery, businessTeams }) => ({
      teamFilter: {
        tags: searchQuery,
        take: defaultTeamQuery.take
      },
      hasMoreToLoad: fp.get('businessTeams.length')(businessTeams) < fp.get('hits')(businessTeams)
    })
  ),
  lifecycle({
    componentDidMount() {
      const {
        business,
        getTeams,
        teamFilter
      } = this.props;
      getTeams(business.id, teamFilter)
    }
  }),
  branch(
    ({ businessTeams }) => !businessTeams,
    renderComponent(Loading)
  ),
  withStateHandlers(
    ({ businessTeams, teamMembers }) => ({
      teams: fp.map(
        (team) => ({
          ...team,
          showMembersRows: false,
          isDeleteModalOpen: false,
          isEditModalOpen: false
        })
      )(businessTeams.businessTeams),
      mappedTeamMembers: fp.flow(
        fp.sortBy([
          (member) => !isBusinessAdmin(member),
          ({ isTeamManager }) => !isTeamManager,
          ({ user }) => fullName(user)
        ]),
        fp.map(
          (member) => ({
            ...member,
            isUpdating: false
          })
        )
      )(teamMembers)
    }),
    {
      setTeamMemberUpdating: ({ mappedTeamMembers }) => (id) => ({
        mappedTeamMembers: fp.map(
          (member) => member.user.id === id ? {
            ...member,
            isUpdating: true
          } : member
        )(mappedTeamMembers)
      }),
      updateTeamMembers: ({ mappedTeamMembers }) => (teamMembers) => ({
        mappedTeamMembers: fp.map(
          (member) => fp.flow(
            fp.find(
              (teamMember) => teamMember.user.id === member.user.id
            ),
            (teamMember) => ({
              ...teamMember,
              isUpdating: false
            })
          )(teamMembers)
        )(mappedTeamMembers)
      }),
      toggleMembersRows: ({ teams, mappedTeamMembers }) => (id) => ({
        teams: fp.map(
          (team) => team.team.id !== id ? team : {
            ...team,
            showMembersRows: !team.showMembersRows
          }
        )(teams),
        mappedTeamMembers: fp.sortBy([
          (member) => !isBusinessAdmin(member),
          ({ isTeamManager }) => !isTeamManager,
          ({ user }) => fullName(user)
        ])(mappedTeamMembers)
      }),
      toggleDeleteModal: ({ teams }) => (id) => ({
        teams: fp.map(
          (team) => team.team.id !== id ? team : {
            ...team,
            isDeleteModalOpen: !team.isDeleteModalOpen
          }
        )(teams)
      }),
      toggleEditModal: ({ teams }) => (id) => ({
        teams: fp.map(
          (team) => team.team.id !== id ? team : {
            ...team,
            isEditModalOpen: !team.isEditModalOpen
          }
        )(teams)
      }),
      updateTeams: ({ teams }) => (businessTeams) => ({
        teams: fp.map(
          (team) => ({
            ...team,
            showMembersRows: fp.flow(
              fp.find(
                (prevTeam) => prevTeam.team.id === team.team.id
              ),
              fp.get('showMembersRows')
            )(teams),
            isDeleteModalOpen: false,
            isEditModalOpen: false
          })
        )(businessTeams.businessTeams)
      })
    }
  ),
  withStateHandlers(
    {
      searchDebounce: undefined
    },
    {
      updateSearchDebounce: () => (searchDebounce) => ({
        searchDebounce
      })
    }
  ),
  lifecycle({
    componentDidUpdate(prevProps) {
      const {
        business,
        getTeams,
        teamFilter,
        updateSearchDebounce,
        searchDebounce,
        refetch
      } = this.props;

      if (JSON.stringify(this.props.businessTeams) !== JSON.stringify(prevProps.businessTeams)) {
        this.props.updateTeams(this.props.businessTeams);
      }

      if (JSON.stringify(this.props.teamMembers) !== JSON.stringify(prevProps.teamMembers)) {
        this.props.updateTeamMembers(this.props.teamMembers);
      }

      if (!fp.equals(prevProps.teamFilter)(teamFilter) && refetch) {
        const debounced = fp.debounce(500)(() => {
          getTeams(business.id, teamFilter)
        });
        updateSearchDebounce(debounced)
        searchDebounce && searchDebounce.cancel();
        debounced();
      }
    }
  }),
  withHandlers({
    loadMoreTeams: ({ loadMoreTeams, business, teamFilter, teams }) => () => {
      loadMoreTeams(business.id, {
        ...teamFilter,
        skip: teams.length
      })
    }
  }),
  withProps(
    ({ teams, toggleDeleteModal, resetSearchQuery, toggleEditModal, removeTeam, mappedTeamMembers, toggleMembersRows, addTeamManager, removeTeamManager, business, setTeamMemberUpdating }) => ({
      rows: fp.flow(
        noCapMap(
          ({ team, members, isDeleteModalOpen, isEditModalOpen, showMembersRows }, rid) => [<Row key={rid} className='no-gutter striped'>
            <Col xs={12} sm={9}>
              <div className='left'>
                <TeamAvatar teamName={he.decode(team.name)} />
              </div>
              <p>
                <Link to={routes.app.teams.teamMembers(team.id)}>{he.decode(team.name)}</Link>
                <br />
                {
                  members.length > 0 ?
                    <Button className='toggle-team-member' bsStyle='plain' onClick={() => toggleMembersRows(team.id)}>
                      <small>{members.length} team {pluralise('member', members.length)}</small>
                    </Button> :
                    <small>0 team members</small>
                }
              </p>
            </Col>
            <Col xs={12} sm={1}>
              <Modal show={isEditModalOpen} onHide={() => toggleEditModal(team.id)} dialogClassName='create-team-modal'>
                <Modal.Body>
                  <Button onClick={() => toggleEditModal(team.id)} className='pull-right full-width' bsStyle='plain'>
                    <Icon icon='close' />
                  </Button>
                  <TeamEditCreate
                    currentBusinessTeam={team}
                    existingTeamMembers={fp.filter(
                      ({ teamIDs }) => fp.contains(team.id)(teamIDs)
                    )(mappedTeamMembers)}
                    callback={() => {
                      resetSearchQuery()
                      toggleEditModal(team.id)
                    }} />
                </Modal.Body>
              </Modal>
              <Button bsStyle='plain' className='full align-right' onClick={() => toggleEditModal(team.id)}>Edit</Button>
            </Col>
            <Col xs={12} sm={2}>
              <ConfirmationModal
                show={isDeleteModalOpen}
                actions={[{
                  onConfirmAction: () => {
                    removeTeam(team.businessId, team.id, true)
                    resetSearchQuery()
                    toggleDeleteModal(team.id)
                  },
                  actionLabel: 'Yes, delete this team',
                  actionClass: 'btn-delete'
                }]}
                cancel={() => toggleDeleteModal(team.id)}
                headingText={<span>Delete team</span>}
                message={<p>Removing this team will only delete the team from your subscription, and will not delete any users associated with this account. To confirm you wish to remove this team click yes below.</p>}
                cancelLabel={`Cancel`} />
              <Button bsStyle='plain' className='full align-right warning' onClick={() => toggleDeleteModal(team.id)}>Delete Team</Button>
            </Col>
          </Row >].concat(
            showMembersRows ? fp.flow(
              fp.filter(
                ({ teamIDs }) => fp.contains(team.id)(teamIDs)
              ),
              noCapMap(
                (member, mid) => (
                  <Row key={`${rid}-${mid}`} className='no-gutter striped team-member-row'>
                    <Col xs={12} sm={9}>
                      <p>
                        {fullName(member.user)}
                      </p>
                    </Col>
                    {
                      member.isUpdating ?
                        <Col xs={12} sm={3}>
                          <Spinner name='circle' className='spinner pull-right' fadeIn='none' />
                        </Col> :
                        isBusinessAdmin(member) ?
                          <Col xs={12} sm={3}>
                            <div className='tags-input'>
                              <div className='bootstrap-tagsinput'>
                                <span className='tag label label-info'>
                                  Business manager
                                </span>
                              </div>
                            </div>
                          </Col> :
                          !member.isTeamManager ?
                            <Col xs={12} sm={3}>
                              <Button bsStyle='plain' className='full align-right' onClick={() => {
                                setTeamMemberUpdating(member.user.id)
                                addTeamManager(business.id, team.id, member.user)
                              }}>
                                Make team manager
                            </Button>
                            </Col> :
                            <Col xs={12} sm={3}>
                              <div className='tags-input'>
                                <div className='bootstrap-tagsinput'>
                                  <span className='tag label label-info'>
                                    Team manager
                                    <span data-role='remove' aria-label={'Remove role'} onClick={() => {
                                      setTeamMemberUpdating(member.user.id)
                                      removeTeamManager(business.id, team.id, member.user)
                                    }} />
                                  </span>
                                </div>
                              </div>
                            </Col>
                    }
                  </Row>
                )
              )
            )(mappedTeamMembers) : []
          )
        ),
        fp.flatten,
        (rows) => rows.length > 0 ? rows : [<Row key='no-team-placeholder' className='no-gutter striped'>
          <Col xs={12}>
            <p className='text-center'>
              No teams found.
            </p>
          </Col>
        </Row>]
      )(teams)
    })
  )
)(Teams);


// WEBPACK FOOTER //
// ./src/containers/pages/app/account-settings/partials/teams.js